import React, { useEffect, useState } from 'react';
import { Box, Card, Typography, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getDashboardSectorAnalyticsByState } from '../../../../helpers/api';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import { PieChartOutline, FiberManualRecord } from '@mui/icons-material';
// Import Recharts components
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';

// --- 1. DEFINE MODERN GRADIENT COLORS ---
const GRADIENTS = [
  { id: 'gradBlue', start: '#00C6FF', end: '#0072FF' }, // Electric Blue
  { id: 'gradPurple', start: '#8E2DE2', end: '#4A00E0' }, // Deep Purple
  { id: 'gradOrange', start: '#F5AF19', end: '#F12711' }, // Warm Orange
  { id: 'gradGreen', start: '#11998E', end: '#38EF7D' }, // Success Green
  { id: 'gradPink', start: '#FF416C', end: '#FF4B2B' }, // Vibrant Pink
  { id: 'gradTeal', start: '#00B4DB', end: '#0083B0' }, // Ocean Teal
];

// --- 2. CUSTOM MUI TOOLTIP ---
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const dataPoint = payload[0];
    return (
      <Paper
        elevation={4}
        sx={{
          p: 2,
          borderRadius: '12px',
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" color="textSecondary" fontWeight={600} mb={0.5}>
          {dataPoint.name}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" fontWeight={700} sx={{ color: dataPoint.payload.fill }}>
            {Intl.NumberFormat('en-NG').format(dataPoint.value)}
          </Typography>
          <Typography variant="caption" fontWeight={600} color="textSecondary" sx={{ mt: 0.5 }}>
            Facilities
          </Typography>
        </Box>
      </Paper>
    );
  }
  return null;
};

// --- 3. CUSTOM LEGEND ---
const CustomLegend = ({ payload }) => {
  const theme = useTheme();
  return (
    <Box display="flex" flexWrap="wrap" justifyContent="center" gap={2} mt={2} px={2}>
      {payload.map((entry, index) => (
        <Box key={`legend-${index}`} display="flex" alignItems="center" gap={0.5}>
          <FiberManualRecord
            sx={{ color: GRADIENTS[index % GRADIENTS.length].end, fontSize: '14px' }}
          />
          <Typography variant="caption" fontWeight={600} color="textSecondary">
            {entry.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// --- 4. CUSTOM LABEL FOR PERCENTAGES INSIDE SLICES ---
const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
  // If the slice is less than 5% of the total, hide the label so it doesn't overlap or look squished
  if (percent < 0.05) return null;

  // Calculate the exact center point inside the slice
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontWeight={700}
      fontSize={13}
      style={{ pointerEvents: 'none', textShadow: '0px 2px 4px rgba(0,0,0,0.3)' }} // Adds readability over light gradients
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export default function SectorAnalyticsRegulatorView({ stateId }) {
  // --- 5. YOUR EXACT CORE FUNCTIONALITY (UNTOUCHED) ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [stateId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getDashboardSectorAnalyticsByState('', stateId, '');
      const responseData = response?.result?.data || [];

      const mappedData = responseData.map((item) => ({
        name: item.sectorName,
        count: item.facilityCount,
      }));

      setData(mappedData);
    } catch (error) {
      console.error('Error fetching sector analytics:', error);
    } finally {
      setLoading(false);
    }
  };
  // ----------------------------------------------------

  // --- 6. NEW ATTRACTIVE CHART RENDERING ---
  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ p: 2, height: '350px' }}>
          <LoaderSkeleton />
          <LoaderSkeleton />
        </Box>
      );
    }

    if (!loading && data.length > 0) {
      return (
        <Box sx={{ width: '100%', height: '350px', position: 'relative' }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <defs>
                {GRADIENTS.map((grad) => (
                  <linearGradient key={grad.id} id={grad.id} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={grad.start} stopOpacity={1} />
                    <stop offset="100%" stopColor={grad.end} stopOpacity={1} />
                  </linearGradient>
                ))}
              </defs>

              <Pie
                data={data}
                dataKey="count"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={80}
                outerRadius={120}
                paddingAngle={4}
                cornerRadius={8}
                stroke="none"
                labelLine={false} // Turns off the ugly black lines connecting outside labels
                label={renderCustomizedLabel} // Injects our new percentage labels
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={`url(#${GRADIENTS[index % GRADIENTS.length].id})`}
                  />
                ))}
              </Pie>

              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
              <Legend content={<CustomLegend />} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          height: '350px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.disabled',
          opacity: 0.7,
        }}
      >
        <PieChartOutline sx={{ fontSize: 60, mb: 1 }} />
        <Typography variant="body1" fontWeight={500}>
          No sector data available
        </Typography>
      </Box>
    );
  };

  return (
    <Card
      elevation={0}
      sx={{
        p: 3,
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.03)',
        bgcolor: '#ffffff',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box mb={1}>
        <Typography variant="h6" fontWeight={700} color="text.primary">
          Industries by Sector
        </Typography>
        <Typography variant="body2" color="textSecondary" mt={0.5}>
          Facility distribution across active sectors
        </Typography>
      </Box>

      <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>{renderContent()}</Box>
    </Card>
  );
}
